import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";

const BE = process.env.REACT_APP_BACKEND_URL || "";
const src = (u) => (u && u.startsWith("/api") ? BE + u : u);

// Auto-rotating holiday-packages banner carousel with Ken-Burns zoom + crossfade (up to 3 slides).
export default function HolidayBannerCarousel() {
  const [cfg, setCfg] = useState(null);
  const [idx, setIdx] = useState(0);
  const tmr = useRef(null);

  useEffect(() => { api.get("/travel/holiday-banner").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const slides = cfg?.slides || [];
  useEffect(() => {
    if (slides.length < 2) return;
    if (tmr.current) clearInterval(tmr.current);
    tmr.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), cfg?.interval_ms || 5000);
    return () => clearInterval(tmr.current);
  }, [slides.length, cfg?.interval_ms]);

  if (!cfg || !cfg.enabled || slides.length === 0) return null;

  return (
    <div className="relative mb-5 h-44 overflow-hidden rounded-3xl sm:h-56" data-testid="holiday-banner">
      <style>{"@keyframes hbKen{from{transform:scale(1)}to{transform:scale(1.12)}}"}</style>
      {slides.map((s, i) => {
        const active = i === idx;
        return (
          <a key={s.id} href={s.cta_link || "#"} target={s.cta_link ? "_blank" : undefined} rel="noreferrer"
            data-testid={active ? "holiday-banner-active" : undefined}
            className={`group absolute inset-0 block transition-opacity duration-700 ${active ? "z-10 opacity-100" : "z-0 opacity-0 pointer-events-none"}`}>
            <img src={src(s.image_url)} alt={s.heading || "Holiday packages"}
              data-testid={active ? "holiday-banner-img" : undefined}
              className="h-full w-full object-cover" style={{ animation: "hbKen 14s ease-in-out infinite alternate", willChange: "transform" }} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center gap-1 p-6 text-white">
              {s.heading && <h2 className="max-w-lg text-2xl font-bold drop-shadow sm:text-3xl" data-testid={active ? "holiday-banner-heading" : undefined}>{s.heading}</h2>}
              {s.subheading && <p className="max-w-md text-sm text-white/90 drop-shadow">{s.subheading}</p>}
              {s.cta_label && <span className="mt-2 inline-flex w-fit items-center gap-1 rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-slate-900 transition group-hover:gap-2">{s.cta_label} <ArrowRight className="h-4 w-4" /></span>}
            </div>
          </a>
        );
      })}
      {slides.length > 1 && (
        <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5" data-testid="holiday-banner-dots">
          {slides.map((sl, i) => (
            <button key={sl.id} onClick={() => setIdx(i)} data-testid={`holiday-banner-dot-${i}`} aria-label={`Slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`} />
          ))}
        </div>
      )}
    </div>
  );
}
